/**
 * MeetingRecordSummary — what this meeting produced, from the one registry (TASK-705).
 *
 * The epic's completion criterion: *opening a meeting shows its brief, its documentation
 * source (labelled as a recorded transcript or an RM-dictated summary — never conflated), its
 * notes, its tasks, its life events and its FINSA notes, from ONE place, keyed by session.*
 * The review tabs below this read their own narrower endpoints; this strip is the record.
 *
 * The load-bearing behaviour is the FAILED state. A section that could not be read is named
 * explicitly, because this epic exists precisely because an empty surface was read as an
 * absence of work — "no notes" and "the notes reader threw" must never render alike.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ClipboardList, FileText, Mic } from 'lucide-react';

import { postMeetingApi, type MeetingRecord } from '../../../services/api/postMeetingApi';

/** Sections the RM cares about, in the order the meeting produced them. */
const COUNTED: Array<[keyof MeetingRecord & string, string]> = [
  ['notes', 'notes'],
  ['tasks', 'tasks'],
  ['life_events', 'life events'],
];

function sectionLabel(key: string): string {
  return key.replace(/_/g, ' ');
}

/**
 * The brief and the notes are the two sections of the record with no tab of their own — the
 * review below has Summary / Tasks / Compliance / Follow-up / Client DNA and nothing else. They
 * were rendered here as bare COUNTS, which is not "opening the meeting shows its brief and its
 * notes": a number is a claim about content, not the content. Both are already in the record
 * this component fetched, so they open in place rather than behind another request.
 */
type Panel = 'notes' | 'brief' | null;

function asStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

export function MeetingRecordSummary({ sessionId }: { sessionId: string }) {
  const [panel, setPanel] = useState<Panel>(null);
  const toggle = (next: Exclude<Panel, null>) =>
    setPanel((prev) => (prev === next ? null : next));
  const query = useQuery({
    queryKey: ['meeting-record', sessionId],
    queryFn: () => postMeetingApi.getMeetingRecord(sessionId),
  });

  if (query.isLoading) {
    return (
      <p className="px-3 py-2 text-xs text-muted-foreground">Loading the meeting record…</p>
    );
  }
  if (query.isError || !query.data) {
    return (
      <p
        data-testid="meeting-record-error"
        className="px-3 py-2 text-xs text-amber-800"
        role="status"
      >
        The meeting record could not be loaded. What follows may be incomplete.
      </p>
    );
  }

  const record = query.data;
  const dictated = record.documentation_sources.find((s) => s.kind === 'rm_dictated_summary');
  const recorded = record.documentation_sources.find((s) => s.kind === 'recorded_transcript');
  const failed = Object.entries(record.sections)
    .filter(([, state]) => state === 'failed')
    .map(([key]) => sectionLabel(key));

  return (
    // `data-session`: the dock resolves the meeting SERVER-side, so reading it back off the
    // surface is the only way to prove it opened the right one.
    <div
      className="border-b border-border px-3 py-2"
      data-testid="meeting-record-summary"
      data-session={sessionId}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {/* The provenance label, never conflated (TASK-704 owns the wording). */}
        {recorded && (
          <span className="inline-flex items-center gap-1" data-testid="record-source-recorded">
            <Mic className="h-3 w-3" aria-hidden="true" />
            Recorded transcript · {recorded.segment_count ?? 0} segments
          </span>
        )}
        {dictated && (
          <span className="inline-flex items-center gap-1" data-testid="record-source-dictated">
            <FileText className="h-3 w-3" aria-hidden="true" />
            {dictated.label}
          </span>
        )}
        {!recorded && !dictated && record.sections.documentation_sources === 'empty' && (
          <span data-testid="record-source-none">No documentation was captured.</span>
        )}

        {COUNTED.map(([key, label]) => {
          const state = record.sections[key];
          if (state === 'failed') return null;
          const count = (record[key] as unknown[]).length;
          const text = `${count} ${count === 1 ? label.replace(/s$/, '') : label}`;
          // Notes have no tab below, so the count opens them. The other two do.
          if (key !== 'notes' || count === 0) {
            return (
              <span key={key} data-testid={`record-count-${key}`}>
                {text}
              </span>
            );
          }
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle('notes')}
              aria-expanded={panel === 'notes'}
              data-testid="record-count-notes"
              className="underline decoration-dotted underline-offset-2 hover:text-foreground"
            >
              {text}
            </button>
          );
        })}

        {record.sections.preparation === 'available' && (
          <button
            type="button"
            onClick={() => toggle('brief')}
            aria-expanded={panel === 'brief'}
            data-testid="record-brief-toggle"
            className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2 hover:text-foreground"
          >
            <ClipboardList className="h-3 w-3" aria-hidden="true" />
            preparation brief
          </button>
        )}

        {record.batch && (
          <span data-testid="record-batch">
            analysis {String((record.batch as { status?: string }).status ?? 'unknown')}
          </span>
        )}
        {record.meeting.retention_until && (
          <span data-testid="record-retention">
            retained until {record.meeting.retention_until.slice(0, 10)}
          </span>
        )}
      </div>

      {panel === 'notes' && (
        <ul className="mt-2 space-y-2" data-testid="record-notes-panel">
          {(record.notes as Array<Record<string, unknown>>).map((note, i) => (
            <li key={String(note.id ?? i)} className="rounded border border-border p-2">
              {/* The honesty label travels with the words wherever they are read. */}
              {note.note_source_type === 'rm_dictation' && (
                <p className="text-[11px] text-muted-foreground">
                  RM-dictated summary — not a verbatim transcript
                </p>
              )}
              <p className="whitespace-pre-wrap text-xs text-foreground">
                {String(note.summary || note.content || '').trim() || 'This note has no text.'}
              </p>
            </li>
          ))}
        </ul>
      )}

      {panel === 'brief' && (
        <div className="mt-2 rounded border border-border p-2" data-testid="record-brief-panel">
          {(() => {
            const brief = ((record.preparation ?? {}) as Record<string, unknown>)
              .briefContent as Record<string, unknown> | undefined;
            const summary = typeof brief?.executiveSummary === 'string' ? brief.executiveSummary : '';
            const points = asStrings(brief?.keyPoints);
            if (!summary && points.length === 0) {
              return (
                <p className="text-xs text-muted-foreground">
                  The brief was generated but carries no readable content.
                </p>
              );
            }
            return (
              <>
                {summary && <p className="whitespace-pre-wrap text-xs text-foreground">{summary}</p>}
                {points.length > 0 && (
                  <ul className="ml-4 list-disc text-xs text-foreground">
                    {points.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                )}
              </>
            );
          })()}
        </div>
      )}

      {failed.length > 0 && (
        <p
          data-testid="meeting-record-failed-sections"
          className="mt-1 inline-flex items-start gap-1 text-[11px] text-amber-800"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
          Could not read: {failed.join(', ')}. These are not empty — they could not be read.
        </p>
      )}
    </div>
  );
}

export default MeetingRecordSummary;
